import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useChatRoomMembers } from '@/hooks/useChatRoomMembers';
import { UserAvatar } from '@/components/UserAvatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { 
  Users, 
  Crown, 
  Shield,
  UserPlus, 
  Settings,
  Users as UsersIcon
} from 'lucide-react';
import UserSearchModal from './UserSearchModal';
import StatusIndicator from './StatusIndicator';
import AdminMemberActions from './AdminMemberActions';

interface EnhancedChatRoomMembersProps {
  roomId: string;
  roomCreatedBy: string | null;
  isPrivate: boolean;
}

export default function EnhancedChatRoomMembers({ 
  roomId, 
  roomCreatedBy, 
  isPrivate 
}: EnhancedChatRoomMembersProps) {
  const { user } = useAuth();
  const [selectedMember, setSelectedMember] = useState<string | null>(null);
  const {
    members,
    isLoading,
    error,
    removeMember,
    updateMemberRole,
    isRemovingMember,
    isUpdatingRole
  } = useChatRoomMembers(roomId);

  const currentUserMember = members.find(m => m.user_id === user?.id);
  const isRoomCreator = roomCreatedBy === user?.id;
  const isCurrentUserAdmin = currentUserMember?.role === 'admin' || isRoomCreator;
  const isCurrentUserModerator = currentUserMember?.role === 'moderator' || isCurrentUserAdmin;

  const handleUserAdded = () => {
    console.log('User added to room, members will be refreshed');
  };

  const handlePromoteMember = async (userId: string, role: 'admin' | 'moderator') => {
    await updateMemberRole({ userId, role });
  };

  const handleDemoteMember = async (userId: string) => {
    await updateMemberRole({ userId, role: 'member' });
  };

  const canManageMember = (targetMember: typeof members[0]) => {
    if (targetMember.user_id === user?.id) return false; // Can't manage yourself
    if (targetMember.user_id === roomCreatedBy) return false; // Can't manage room creator
    
    if (isRoomCreator) return true; // Room creator can manage anyone
    if (isCurrentUserAdmin && targetMember.role !== 'admin') return true; // Admins can manage non-admins
    if (isCurrentUserModerator && targetMember.role === 'member') return true; // Moderators can manage members
    
    return false;
  };

  const getUserDisplayName = (member: typeof members[0]) => {
    const profile = member.profiles;
    if (profile?.first_name || profile?.last_name) {
      return `${profile.first_name || ''} ${profile.last_name || ''}`.trim();
    }
    return 'Unknown User';
  };

  const getUserInitials = (member: typeof members[0]) => {
    const profile = member.profiles;
    if (profile?.first_name || profile?.last_name) {
      return `${profile.first_name?.[0] || ''}${profile.last_name?.[0] || ''}`.toUpperCase();
    }
    return 'UN';
  };

  // Group members by role
  const membersByRole = {
    creator: members.filter(m => m.user_id === roomCreatedBy),
    admins: members.filter(m => m.role === 'admin' && m.user_id !== roomCreatedBy),
    moderators: members.filter(m => m.role === 'moderator'),
    members: members.filter(m => m.role === 'member')
  };

  const MemberCard = ({ member, showRole = true }: { member: typeof members[0]; showRole?: boolean }) => (
    <div className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/50 transition-colors">
      <div className="flex items-center space-x-3 flex-1 min-w-0">
        <div className="relative">
          <UserAvatar
            userId={member.user_id}
            fallback={getUserInitials(member)}
            size="md"
            showLevel={true}
          />
          <div className="absolute -bottom-0.5 -right-0.5">
            <StatusIndicator status="online" size="sm" />
          </div>
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="font-medium truncate">{getUserDisplayName(member)}</p>
            {member.user_id === user?.id && (
              <Badge variant="secondary" className="text-xs">You</Badge>
            )}
          </div>
          
          {showRole && (
            <div className="flex items-center space-x-2 mt-1">
              {member.user_id === roomCreatedBy && (
                <Badge className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white border-0 text-xs">
                  <Crown className="h-3 w-3 mr-1" />
                  Creator
                </Badge>
              )}
              {member.role === 'admin' && member.user_id !== roomCreatedBy && (
                <Badge className="bg-gradient-to-r from-anyskill-purple to-anyskill-blue text-white border-0 text-xs">
                  <Shield className="h-3 w-3 mr-1" />
                  Admin
                </Badge>
              )}
              {member.role === 'moderator' && (
                <Badge variant="outline" className="text-xs border-green-500 text-green-700 dark:text-green-400">
                  <Shield className="h-3 w-3 mr-1" />
                  Moderator
                </Badge>
              )}
            </div>
          )}
        </div>
      </div>

      {canManageMember(member) && (
        <AdminMemberActions
          member={member}
          currentUserId={user?.id || ''}
          roomCreatedBy={roomCreatedBy}
          isRoomCreator={isRoomCreator}
          isCurrentUserAdmin={isCurrentUserAdmin}
          isCurrentUserModerator={isCurrentUserModerator}
          onPromoteMember={handlePromoteMember}
          onDemoteMember={handleDemoteMember}
          onRemoveMember={removeMember}
        />
      )}
    </div>
  );

  if (isLoading) {
    return (
      <Card className="w-80">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Users className="h-5 w-5" />
            <span>Members</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-anyskill-purple"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="w-80">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Users className="h-5 w-5" />
            <span>Members</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-4">
            <p className="text-muted-foreground">Failed to load members</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-80 h-full flex flex-col">
      <CardHeader className="flex-shrink-0">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center space-x-2">
            <Users className="h-5 w-5" />
            <span>Members ({members.length})</span>
          </CardTitle>
          
          {(isCurrentUserAdmin || isPrivate) && (
            <UserSearchModal roomId={roomId} onUserAdded={handleUserAdded}>
              <Button size="sm" variant="outline" className="flex items-center gap-2">
                <UserPlus className="h-4 w-4" />
                Invite
              </Button>
            </UserSearchModal>
          )}
        </div>
      </CardHeader>

      <CardContent className="flex-1 overflow-hidden p-0">
        <ScrollArea className="h-full px-4 pb-4">
          <div className="space-y-4">
            {/* Room Creator */}
            {membersByRole.creator.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-muted-foreground mb-2 px-2">
                  Room Creator
                </h4>
                <div className="space-y-2">
                  {membersByRole.creator.map((member) => (
                    <MemberCard key={member.id} member={member} />
                  ))}
                </div>
              </div>
            )}

            {/* Admins */}
            {membersByRole.admins.length > 0 && (
              <>
                {membersByRole.creator.length > 0 && <Separator />}
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground mb-2 px-2">
                    Admins ({membersByRole.admins.length})
                  </h4>
                  <div className="space-y-2">
                    {membersByRole.admins.map((member) => (
                      <MemberCard key={member.id} member={member} />
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* Moderators */}
            {membersByRole.moderators.length > 0 && (
              <>
                {(membersByRole.creator.length > 0 || membersByRole.admins.length > 0) && <Separator />}
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground mb-2 px-2">
                    Moderators ({membersByRole.moderators.length})
                  </h4>
                  <div className="space-y-2">
                    {membersByRole.moderators.map((member) => (
                      <MemberCard key={member.id} member={member} />
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* Regular Members */}
            {membersByRole.members.length > 0 && (
              <>
                {(membersByRole.creator.length > 0 || membersByRole.admins.length > 0 || membersByRole.moderators.length > 0) && <Separator />}
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground mb-2 px-2">
                    Members ({membersByRole.members.length})
                  </h4>
                  <div className="space-y-2">
                    {membersByRole.members.map((member) => (
                      <MemberCard key={member.id} member={member} />
                    ))}
                  </div>
                </div>
              </>
            )}

            {members.length === 0 && (
              <div className="text-center py-8">
                <Users className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">No members found</p>
              </div>
            )}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
}